import { PlatformError } from '@djgg/core';

export const CREATION_ARTIFACT_SCHEMA_VERSION = 1 as const;
export const EARLY_IMAGE_BUDGET_CEILING_MICROS = 1_000_000;

export type CreationTitleAuthorityV1 =
  | { kind: 'temporary'; runId: string; setAt: number }
  | { kind: 'automatic'; runId: string; selectionArtifactId: string; setAt: number }
  | { kind: 'creator'; setAt: number };

export type CreationArtifactRunOriginV1 = 'create' | 'film';
export const CREATION_RUN_MOMENTS_V1 = [
  'opened', 'first-artifact', 'title-selected', 'first-visual', 'first-binding',
] as const;
export type CreationRunMomentV1 = (typeof CREATION_RUN_MOMENTS_V1)[number];
export type CreationRunMomentsV1 = Partial<Record<CreationRunMomentV1, number>>;

export interface CreationArtifactRunV1 {
  schemaVersion: 1;
  runId: string;
  creationId: string;
  origin: CreationArtifactRunOriginV1;
  requestFingerprint?: string | null;
  moments: CreationRunMomentsV1;
  nextArrivalSeq: number;
  createdAt: number;
  updatedAt: number;
}

export type CreationArtifactKindV1 =
  | 'idea-receipt'
  | 'title-candidate'
  | 'title-selection'
  | 'tagline'
  | 'palette'
  | 'motif'
  | 'cast-vocabulary'
  | 'core-verb'
  | 'player-fantasy'
  | 'visual-brief'
  | 'design-card'
  | 'reference-summary'
  | 'reference-media'
  | 'image-original'
  | 'image-derivative'
  | 'sound-idea'
  | 'sound-original'
  | 'sound-derivative'
  | 'transformation'
  | 'proof'
  | 'film-material';

export type CreationArtifactDigestV1 =
  | { algorithm: 'sha256'; value: string }
  | { algorithm: 'canonical-record-sha256-v1'; value: string }
  | { algorithm: 'canonical-record-md5-v1'; value: string };

export interface CreationArtifactProvenanceV1 {
  origin: 'creator' | 'generated' | 'reference' | 'platform' | 'capture';
  provider?: string;
  model?: string;
  label?: string;
}

export type CreationArtifactEligibilityV1 = 'reusable' | 'inspiration-only';
export type CreationArtifactLifecycleV1 = 'candidate' | 'accepted' | 'rejected';

/** Public-safe durable authority. Raw provider requests/replies and cost data
 * belong only to CreationProviderDispatchV1. */
export interface CreationArtifactV1 {
  schemaVersion: 1;
  artifactId: string;
  creationId: string;
  runId: string;
  logicalKey: string;
  kind: CreationArtifactKindV1;
  title: string;
  payload?: unknown;
  uri?: string;
  contentType?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  contentDigest: CreationArtifactDigestV1;
  provenance: CreationArtifactProvenanceV1;
  eligibility: CreationArtifactEligibilityV1;
  roleHints: string[];
  lifecycle: CreationArtifactLifecycleV1;
  quality?: { status: 'pending' | 'accepted' | 'rejected'; reason?: string };
  safety?: { status: 'pending' | 'accepted' | 'rejected'; reason?: string };
  arrivedAt: number;
  arrivalSeq: number;
}

export interface CreationArtifactEdgeV1 {
  schemaVersion: 1;
  edgeId: string;
  creationId: string;
  runId: string;
  parentArtifactId: string;
  childArtifactId: string;
  parentIndex: number;
  relation: 'crop' | 'cutout' | 'recolor' | 'combine' | 'derive' | 'reference';
  parametersDigest?: string;
  createdAt: number;
}

export type CreationArtifactUsageKindV1 =
  | 'direct'
  | 'transformed'
  | 'palette-source'
  | 'style-source'
  | 'reference-source'
  | 'secondary'
  | 'retained';

export interface CreationArtifactUsageV1 {
  schemaVersion: 1;
  usageId: string;
  creationId: string;
  runId: string;
  artifactId: string;
  artifactEligibility: CreationArtifactEligibilityV1;
  kind: CreationArtifactUsageKindV1;
  targetRole: string;
  targetPath?: string;
  version?: number;
  reason?: string;
  createdAt: number;
}

export type CreationProviderDispatchLaneV1 = 'spark' | 'early-image';
export type CreationProviderDispatchSlotV1 = 'spark' | 'world-foundation' | 'identity-foundation';

/** Server-only provider authority. Never expose this row through browser RLS. */
export interface CreationProviderDispatchV1 {
  schemaVersion: 1;
  dispatchId: string;
  creationId: string;
  runId: string;
  lane: CreationProviderDispatchLaneV1;
  slot: CreationProviderDispatchSlotV1;
  status: 'queued' | 'claimed' | 'dispatched' | 'succeeded' | 'failed';
  maxAttempts: 1;
  attempts: number;
  idempotencyKey: string;
  requestFingerprint: string;
  provider?: string;
  providerRequestId?: string;
  claimToken?: string;
  reservedMicros: number;
  actualMicros?: number;
  responseArtifactId?: string;
  errorCode?: string;
  createdAt: number;
  updatedAt: number;
}

/** Server-only pooled budget for exactly the two early-image slots. */
export interface CreationImageBudgetV1 {
  schemaVersion: 1;
  runId: string;
  creationId: string;
  ceilingMicros: number;
  reservedMicros: number;
  spentMicros: number;
  reservedSlots: CreationProviderDispatchSlotV1[];
  createdAt: number;
  updatedAt: number;
}

function fail(message: string): never {
  throw new PlatformError('validation', `invalid creation artifact: ${message}`);
}

function id(value: unknown, label: string, max = 500): asserts value is string {
  if (typeof value !== 'string' || value.length < 1 || value.length > max || value.includes('/')) {
    fail(`${label} must be a path-safe 1..${max} character string`);
  }
}

function time(value: unknown, label: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) fail(`${label} must be a timestamp`);
}

export function creationArtifactRunIdentityV1(creationId: string): string {
  id(creationId, 'creationId', 200);
  return `create-run:${creationId}`;
}

export function creationArtifactIdV1(runId: string, logicalKey: string): string {
  id(runId, 'runId', 200);
  id(logicalKey, 'logicalKey', 240);
  return `artifact:${runId}:${logicalKey}`;
}

export function creationArtifactEdgeIdV1(childArtifactId: string, parentArtifactId: string, parentIndex: number): string {
  id(childArtifactId, 'childArtifactId');
  id(parentArtifactId, 'parentArtifactId');
  if (!Number.isInteger(parentIndex) || parentIndex < 0) fail('parentIndex must be a non-negative integer');
  return `edge:${childArtifactId}:${parentIndex}:${parentArtifactId}`;
}

export function creationProviderDispatchIdV1(
  runId: string,
  lane: CreationProviderDispatchLaneV1,
  slot: CreationProviderDispatchSlotV1,
): string {
  id(runId, 'runId', 200);
  return `dispatch:${runId}:${lane}:${slot}`;
}

export function validateCreationArtifactRunV1(value: CreationArtifactRunV1): CreationArtifactRunV1 {
  if (value.schemaVersion !== 1) fail('run schemaVersion must be 1');
  id(value.runId, 'runId', 200);
  id(value.creationId, 'creationId', 200);
  if (!['create', 'film'].includes(value.origin)) fail('run origin is invalid');
  if (value.requestFingerprint != null && !/^[a-f0-9]{64}$/.test(value.requestFingerprint)) fail('request fingerprint is invalid');
  if (!value.moments || typeof value.moments !== 'object' || Array.isArray(value.moments)) fail('run moments are invalid');
  for (const [moment, at] of Object.entries(value.moments)) {
    if (!(CREATION_RUN_MOMENTS_V1 as readonly string[]).includes(moment)) fail('run moment is invalid');
    time(at, `moments.${moment}`);
  }
  if (!Number.isInteger(value.nextArrivalSeq) || value.nextArrivalSeq < 0) fail('nextArrivalSeq must be non-negative');
  time(value.createdAt, 'createdAt');
  time(value.updatedAt, 'updatedAt');
  return value;
}

export function validateCreationArtifactV1(value: CreationArtifactV1): CreationArtifactV1 {
  if (value.schemaVersion !== 1) fail('schemaVersion must be 1');
  id(value.artifactId, 'artifactId');
  id(value.creationId, 'creationId', 200);
  id(value.runId, 'runId', 200);
  id(value.logicalKey, 'logicalKey', 240);
  if (value.artifactId !== creationArtifactIdV1(value.runId, value.logicalKey)) fail('artifactId does not match runId/logicalKey');
  if (typeof value.title !== 'string' || !value.title.trim() || value.title.length > 200) fail('title is invalid');
  const expectedDigestLength = value.contentDigest.algorithm === 'canonical-record-md5-v1' ? 32 : 64;
  if (!new RegExp(`^[a-f0-9]{${expectedDigestLength}}$`).test(value.contentDigest.value)) fail('content digest is invalid');
  if (!['reusable', 'inspiration-only'].includes(value.eligibility)) fail('eligibility is invalid');
  if (!['candidate', 'accepted', 'rejected'].includes(value.lifecycle)) fail('lifecycle is invalid');
  if (!Array.isArray(value.roleHints) || value.roleHints.some(role => typeof role !== 'string' || !role || role.length > 100)) fail('roleHints are invalid');
  time(value.arrivedAt, 'arrivedAt');
  if (!Number.isInteger(value.arrivalSeq) || value.arrivalSeq < 1) fail('arrivalSeq must be positive');
  return value;
}
